import React, { createContext, useContext, ReactNode } from 'react';
import { Company, QuotaUsageKey, Subscription } from '@esg/esg-global-types';
import { CompanyContext } from './CompanyContext';
import { ExceededQuotaKey } from '../lib/app/subscription';

interface SubscriptionContextProps {
  subscription: Subscription | null;
  incrementSubscriptionUsage: (keys: Array<QuotaUsageKey>, amount?: number) => void;
  decrementSubscriptionUsage: (keys: Array<QuotaUsageKey>, amount?: number) => void;
  setSubscriptionQuota: (quota_map: Record<QuotaUsageKey, number>) => void;
  getExceededSubscriptionKeys: (keys: Array<QuotaUsageKey>) => Array<ExceededQuotaKey>;
}

interface SubscriptionProviderProps {
  children: ReactNode;
}

const SubscriptionContext = createContext<SubscriptionContextProps | undefined>(undefined);

/**
 * Provides subscription context to child components, initializing from the CompanyContext.
 * @param props Provider props.
 * @returns JSX element wrapping children with subscription context.
 */
export const SubscriptionProvider = ({ children }: SubscriptionProviderProps): JSX.Element => {
  const { company }: { company: Company | null } = React.useContext(CompanyContext);
  const [subscription, setSubscription] = React.useState<Subscription | null>(null);

  // Initialize subscription from the company object.
  React.useEffect(() => {
    if (company?.subscription) {
      setSubscription(company.subscription);
    }
  }, [company]);

  // Increases usage count for the specified subscription quota keys.
  const incrementSubscriptionUsage = (keys: Array<QuotaUsageKey>, amount = 1): void => {
    setSubscription((prev: Subscription | null): Subscription | null => {
      if (!prev) return prev;
      const updated_usage: Partial<Record<QuotaUsageKey, number>> = { ...prev.usage };
      keys.forEach((key: QuotaUsageKey) => {
        updated_usage[key] = (updated_usage[key] ?? 0) + amount;
      });
      return {
        ...prev,
        usage: updated_usage
      };
    });
  };

  // Decreases usage count for the specified subscription quota keys, ensuring it doesn't go below zero.
  const decrementSubscriptionUsage = (keys: Array<QuotaUsageKey>, amount = 1): void => {
    setSubscription((prev: Subscription | null): Subscription | null => {
      if (!prev) return prev;
      const updated_usage: Partial<Record<QuotaUsageKey, number>> = { ...prev.usage };
      keys.forEach((key: QuotaUsageKey) => {
        updated_usage[key] = Math.max(0, (updated_usage[key] ?? 0) - amount);
      });
      return {
        ...prev,
        usage: updated_usage
      };
    });
  };

  // Sets specific quota values for each subscription quota key.
  const setSubscriptionQuota = (quota_map: Record<QuotaUsageKey, number>): void => {
    setSubscription((prev: Subscription | null): Subscription | null => {
      if (!prev) return prev;
      const updated_quota: Partial<Record<QuotaUsageKey, number>> = { ...(prev.quota ?? {}) };

      Object.entries(quota_map).forEach(([key, value]) => {
        updated_quota[key as QuotaUsageKey] = value;
      });

      return {
        ...prev,
        quota: updated_quota
      };
    });
  };

  /**
   * Returns exceeded quota keys along with their title-cased labels.
   * @param keys Array of quota keys to check.
   * @returns Array of objects with exceeded keys and title labels.
   */
  const getExceededSubscriptionKeys = (keys: Array<QuotaUsageKey>): Array<ExceededQuotaKey> => {
    if (!subscription) return [];

    return keys
      .filter((key: QuotaUsageKey): boolean => {
        const quotaLimit = subscription.quota[key];
        return quotaLimit != null && (subscription.usage[key] ?? 0) >= quotaLimit;
      })
      .map(
        (key: QuotaUsageKey): ExceededQuotaKey => ({
          key,
          label: formatQuotaLabel(key)
        })
      );
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        incrementSubscriptionUsage,
        decrementSubscriptionUsage,
        setSubscriptionQuota,
        getExceededSubscriptionKeys
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Custom hook to access subscription context.
 * @throws If called outside of a SubscriptionProvider.
 * @returns Subscription context value.
 */
export const useSubscriptionContext = (): SubscriptionContextProps => {
  const context: SubscriptionContextProps | undefined = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider.');
  }
  return context;
};

/**
 * Converts a quota key to a human-readable Title Case label.
 */
// TODO: Replace with the updated version for formatTitleCase once merged, then remove this function.
const formatQuotaLabel = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters (for camelCase)
    .replace(/_/g, ' ') // Replace underscores with spaces (for snake_case)
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()); // Title Case
};
